import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { FilterOperators, QueryResponse } from "~/modules/filters/types";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Routes } from "~/routes";
import { Footer } from "~/components/Footer";
import { Network } from "~/modules/networks/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { queryNetworks } from "~/modules/networks/api/router";
import { DEFAULT_NETWORK_LIST_QUERY } from "~/modules/networks/consts";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { NetworkListPage } from "~/modules/networks/pages/list/component";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import {
    getNetworkPageDescription,
    NetworkListWebPageSEO,
} from "~/modules/networks/seo/NetworkListWebPageSEO";
import {
    ASSET_CLASS_IDS,
    BOOK_ENTRY_NETWORK_NAME,
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
    SOCIAL_OG_IMAGE_URL,
    MEASURE_IDS,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

export async function getStaticProps() {
    const [
        listQueryResponse,
        // Aggregate values
        distributedAggregates,
        representedAggregates,
        rwaHoldersAggregates,
        stablecoinValueAggregates,
        stablecoinHoldersAggregates,
        netFlowsWithStablecoins,
        netFlows,
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
    ] = await Promise.all([
        queryNetworks({
            query: {
                ...DEFAULT_NETWORK_LIST_QUERY,
                pagination: {
                    page: 1,
                    perPage: 150,
                },
            },
        }),
        // Aggregate values
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "networks-list--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "networks-list--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 3. Total Asset Holders (RWA - excluding stablecoins)
        queryTokenAggregateValueWith30D({
            name: "networks-list--total-rwa-holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 4. Total Stablecoin Value
        queryTokenAggregateValueWith30D({
            name: "networks-list--total-stablecoin-value",
            measureID: MEASURE_IDS.BridgedTokenMarketCapDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // 5. Total Stablecoin Holders
        queryTokenAggregateValueWith30D({
            name: "networks-list--total-stablecoin-holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        queryNetFlows({
            name: "networks-list",
            groupBy: TokenTimeseriesGroupBy.parentNetwork,
        }),
        queryNetFlows({
            name: "networks-list-exclude-stablecoins",
            groupBy: TokenTimeseriesGroupBy.parentNetwork,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
        }),
        // League tables by distribution type (with stablecoins) — feeds the
        // pie chart. Grouped by parent network so child networks roll up into
        // their parents, as the old network pie chart did.
        queryLeagueTablesByDistributionType({
            name: "networks-list",
            groups: [LeagueTableGroupTypes.parentNetworks],
        }),
        // League tables by distribution type (without stablecoins) — feeds the pie chart
        queryLeagueTablesByDistributionType({
            name: "networks-list-exclude-stablecoins",
            filter: {
                field: TokenTimeseriesColumns.assetClassID,
                operator: FilterOperators.NotEquals,
                value: ASSET_CLASS_IDS.Stablecoin,
            },
            groups: [LeagueTableGroupTypes.parentNetworks],
        }),
    ]);

    // Get the list of networks that have child networks
    const paretNetworkIDs = listQueryResponse.results.reduce(
        (all, n) => (n.parent_network_id ? [...all, n.parent_network_id] : all),
        [] as number[],
    );

    // Filter the results to only include networks that have child networks or assets
    const filteredResults = listQueryResponse.results
        .filter((n) => n.name !== BOOK_ENTRY_NETWORK_NAME)
        .filter(
            (n) => paretNetworkIDs.includes(n.id) || (n.asset_count ?? 0) > 0,
        );

    const aggregates: AggregateDisplayProps[] = [
        {
            label: "Distributed Asset Value",
            type: "dollar_compact",
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            value: distributedAggregates.value,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Asset Value",
            type: "dollar_compact",
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            value: representedAggregates.value,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Stablecoin Value",
            type: "dollar_compact",
            value: stablecoinValueAggregates.value,
            percentChange: {
                value: stablecoinValueAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Total RWA Holders",
            type: "count",
            value: rwaHoldersAggregates.value,
            percentChange: {
                value: rwaHoldersAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Total Stablecoin Holders",
            type: "count_compact",
            value: stablecoinHoldersAggregates.value,
            percentChange: {
                value: stablecoinHoldersAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Total Networks",
            type: "count",
            value: filteredResults.length,
        },
    ];

    return {
        props: {
            aggregates,
            netFlows,
            netFlowsWithStablecoins,
            leagueTableTabs,
            leagueTableTabsWithoutStablecoins,
            listQueryResponse: {
                ...listQueryResponse,
                results: filteredResults,
                pagination: {
                    ...listQueryResponse.pagination,
                    resultCount: filteredResults.length,
                },
            },
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function NetworksPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<Network>;
    netFlows: NetFlowsData;
    netFlowsWithStablecoins: NetFlowsData;
    leagueTableTabs: LeagueTableTabsByDistributionType;
    leagueTableTabsWithoutStablecoins: LeagueTableTabsByDistributionType;
}) {
    const {
        aggregates,
        listQueryResponse,
        netFlows,
        netFlowsWithStablecoins,
        leagueTableTabs,
        leagueTableTabsWithoutStablecoins,
    } = props;

    return (
        <PageTrack route={Routes.networks}>
            <NextSeo
                title="Networks"
                canonical={addBaseUrl(Routes.networks)}
                description={getNetworkPageDescription()}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.networks),
                    title: "Networks",
                    site_name: "RWA.xyz",
                    description: getNetworkPageDescription(),
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "blockchain networks, RWA networks, Ethereum, Polygon, Solana, Base, Arbitrum, tokenization networks, real world assets",
                    },
                ]}
            />
            <NetworkListWebPageSEO />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Networks",
                        href: Routes.networks,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.networks} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.networks}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Networks",
                            current: false,
                        },
                    ]}
                />
                <PageWrapper>
                    <NetworkListPage
                        aggregates={aggregates}
                        listQueryResponse={listQueryResponse}
                        netFlows={netFlows}
                        netFlowsWithStablecoins={netFlowsWithStablecoins}
                        leagueTableTabs={leagueTableTabs}
                        leagueTableTabsWithoutStablecoins={
                            leagueTableTabsWithoutStablecoins
                        }
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
